import fetchDataService from "./fetchDataService";

export const CYCLE_ATTACHMENTS_BUCKET = 'cycle-attachments';
export const PRODUCT_IMAGES_BUCKET = 'product-images';
export const USER_PROFILES_BUCKET = 'user-profiles';
export const COMPANY_LOGOS_BUCKET = 'company-logos';

const api_request = async (requestPromise: Promise<any>, errorMessage: string) => {
  try {
    const response = await requestPromise;
    if (response?.error || (response?.status && response.status !== 200)) {
      throw new Error(response.message || response.error || errorMessage);
    }
    return response;
  } catch (err: any) {
    return { error: true, message: err.message || errorMessage };
  }
};

/**
 * Solicita una URL firmada de subida al backend service
 */
const createSignedUploadUrl = async (tokenData: any, requestData: { bucket: string; path: string; upsert?: boolean }) => {
  return await api_request(
    fetchDataService.post('/file-storage/create-signed-upload-url', tokenData.key, {
      ...requestData,
      csrfToken: tokenData.token.uid,
    }),
    "Error al generar URL firmada de subida"
  );
};

/**
 * Consulta la URL pública de un archivo
 */
const getPublicUrl = async (tokenData: any, requestData: { bucket: string; path: string }) => {
  return await api_request(
    fetchDataService.post('/file-storage/get-public-url', tokenData.key, {
      ...requestData,
      csrfToken: tokenData.token.uid,
    }),
    "Error al obtener URL pública del archivo"
  );
};

/**
 * Elimina uno o varios archivos del almacenamiento vía backend service
 */
const deleteFile = async (tokenData: any, requestData: { bucket: string; paths: string[] }) => {
  return await api_request(
    fetchDataService.post('/file-storage/delete', tokenData.key, {
      ...requestData,
      csrfToken: tokenData.token.uid,
    }),
    "Error al eliminar archivo del almacenamiento"
  );
};

/**
 * Mueve o renombra un archivo dentro del mismo bucket vía backend service
 */
const moveFile = async (tokenData: any, requestData: { bucket: string; fromPath: string; toPath: string }) => {
  return await api_request(
    fetchDataService.post('/file-storage/move', tokenData.key, {
      ...requestData,
      csrfToken: tokenData.token.uid,
    }),
    "Error al mover archivo en almacenamiento"
  );
};

const normalizeClientUrl = (url: string): string => {
  if (!url) return url;
  if (url.includes('host.docker.internal')) {
    const host = window.location.hostname || 'localhost';
    return url.replace('host.docker.internal', host);
  }
  return url;
};

/**
 * Flujo completo y seguro para subir un archivo:
 * 1. Obtiene la Signed Upload URL a través del backend service (sin claves expuestas).
 * 2. Hace un PUT directo a Supabase Storage con el binario del archivo.
 * 3. Retorna la URL pública y el storagePath.
 */
const uploadFile = async (
  tokenData: any,
  bucket: string,
  path: string,
  file: File,
  upsert: boolean = false
): Promise<{ url: string; storagePath: string }> => {
  const signedUrlRes = await createSignedUploadUrl(tokenData, {
    bucket,
    path,
    upsert,
  });

  if (signedUrlRes.error || !signedUrlRes.signedUrl) {
    throw new Error(signedUrlRes.message || 'Error al obtener autorización para subir archivo');
  }

  const uploadTargetUrl = normalizeClientUrl(signedUrlRes.signedUrl);

  const uploadResponse = await fetch(uploadTargetUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text().catch(() => '');
    throw new Error(`Error en subida de archivo (${uploadResponse.status}): ${errorText || uploadResponse.statusText}`);
  }

  return {
    url: normalizeClientUrl(signedUrlRes.publicUrl),
    storagePath: signedUrlRes.path || path,
  };
};

const fileStorageService = {
  createSignedUploadUrl,
  getPublicUrl,
  deleteFile,
  moveFile,
  uploadFile,
};

export default fileStorageService;
